/* eslint-disable react/no-danger-with-children */
import cn from 'helpers/cn'
import siteConfig from 'config'
import useTestContext from 'contexts/TestContext/hook'
import { getQuestionNumber, getTestAsset, isCorrect } from 'helpers'
import { FC, Fragment, useLayoutEffect, useState } from 'react'
import Question from './Question'
import { useNavigate } from 'react-router'
import HTML from 'components/HTML'
import Audio from 'components/Audio'
import AlzaBanner from 'components/AlzaBanner'
import GatedExplainButton from 'components/GatedExplainButton'
import { resolveHtml } from 'helpers/explain'
import { IAnswered } from 'types/tests'
import { AlzaClick } from 'helpers/alzaClick'
import DonateModal from 'components/modals/DonateModal'
import { DonateDebounce } from 'helpers/donateDebounce'
import { Adsense } from '@ctrl/react-adsense'

const TestPage: FC = () => {
  const { data: test, started, end, ended, assetContext } = useTestContext()
  const navigate = useNavigate()
  const [showDonate, setShowDonate] = useState(false)

  const [showFormulas, setShowFormulas] = useState(false)
  const isMat = test?.type.endsWith('-mat') && !!siteConfig.features.formulasImageUrl

  useLayoutEffect(() => {
    Array.from(document.body.getElementsByClassName('blank-answer')).forEach((el) => {
      el.innerHTML = '&emsp;&emsp;'
    })
  }, [test])

  useLayoutEffect(() => {
    const test = document.getElementById('test')
    if (!test) return
    const cb = () => {
      const test = document.getElementById('test')
      if (test) {
        const recordings = Array.from(test.getElementsByClassName('recording')).map((el) => el.firstChild as HTMLDivElement)
        const screenRect = document.body.getBoundingClientRect()
        const testTop = screenRect.top
        const testBottom = screenRect.bottom
        recordings.forEach((wrapper) => {
          if (!wrapper) return
          const recordingRect = wrapper.getBoundingClientRect()
          const recordingTop = recordingRect.top
          const recordingBottom = recordingRect.bottom
          if (recordingTop < testTop) {
            wrapper.getElementsByTagName('audio')[0]?.pause()
          } else if (recordingBottom > testBottom) {
            wrapper.getElementsByTagName('audio')[0]?.pause()
          }
        })
      }
    }

    test.addEventListener('scroll', cb)

    return () => {
      test.removeEventListener('scroll', cb)
    }
  }, [started])

  const onEnd = () => {
    // go to alza
    const alzaOpened = AlzaClick.openLink()
    if (!alzaOpened) {
      DonateDebounce.openLink(() => setShowDonate(true))
    }
    end()
  }

  return (
    <div className="w-full h-full relative">
      <DonateModal open={showDonate} close={() => setShowDonate(false)} />
      <div
        id="test"
        className={
          cn('w-full h-full justify-center transition-all bg-back overscroll-contain overflow-x-hidden', started ? 'opacity-1 pointer-events-auto' : 'opacity-0 pointer-events-none overflow-y-hidden')
        }
      >
        <div className="flex flex-col space-y-5 pb-20 divide-y border-slate-200 [&>*:not(:first-child)]:!border-t-0">
          {test?.recording && !ended && (
            <div className="w-full p-4 sticky top-0 z-[100] -mb-10">
              <Audio recording={test.recording} />
            </div>
          )}
          {test?.sections.map((section, i) => (
            <div key={`s_${i}`} className="flex flex-col space-y-5 divide-y border-slate-200 animate-fade-in pt-5">
              {!!section.name && <h2 className="font-semibold uppercase text-2xl text-center select-none py-2 px-2" dangerouslySetInnerHTML={{ __html: section.name || '' }} />}
              {section.parts.map((part, j) => (
                <div key={`p_${i}_${j}`} className="flex flex-col space-y-2 items-center pt-5">
                  {part?.recording && !ended && (
                    <div className="w-full p-4 sticky top-0 z-[100]">
                      <Audio recording={part.recording} />
                    </div>
                  )}
                  {!!part.name && (
                    <h3 className="font-semibold uppercase text-2xl text-center select-none pt-2 pb-6 w-full border-b border-b-slate-200 px-2" dangerouslySetInnerHTML={{ __html: part.name || '' }} />
                  )}
                  {part.description && (
                    <div className="w-full bg-white flex justify-center py-5 relative pb-10 px-5 border-b border-b-slate-200">
                      <img className="w-full mobile:max-w-none mobileWidth:max-w-none max-w-max" src={getTestAsset(part.description)} />
                      <div className="absolute bg-back mix-blend-multiply inset-0" />
                    </div>
                  )}
                  {part.htmlContent && <HTML content={part.htmlContent} />}
                  <div className="px-5 flex flex-col items-start w-full space-y-2 pt-8">
                    {part.questions.map((question, k) => {
                      const q = getQuestionNumber(test, i, j, k)
                      const mod = q % 16
                      const showAlza = mod === 7
                      const showGoogle = (mod === 3 || mod === 15) && started
                      const answered = ended && (question as IAnswered).answered
                      const wrong = answered && !isCorrect(question as IAnswered)
                      const rawPartHtml = Array.isArray(part.htmlContent)
                        ? part.htmlContent.join('\n')
                        : (part.htmlContent ?? '')
                      const partAudioRaw = part.recording ? assetContext[part.recording] : ''
                      const partAudioDesc = partAudioRaw ? `[Audio transcript]: ${partAudioRaw}` : ''
                      const partImgRaw = part.description ? assetContext[part.description] : ''
                      const partImgDesc = partImgRaw ? `[Image text]: ${partImgRaw}` : ''
                      const partHtml = [partAudioDesc, partImgDesc, resolveHtml(rawPartHtml, assetContext)]
                        .filter(Boolean).join('\n\n')
                      const questionHtml = resolveHtml((question as any).question ?? '', assetContext)
                      return (
                        <Fragment key={`q_${i}_${j}_${k}`}>
                          <Question question={question} id={`q_${i}_${j}_${k}`} number={q} showResult={ended} />
                          {wrong && (
                            <div className="px-5 -mt-2 mb-2">
                              <GatedExplainButton
                                question={questionHtml}
                                partHtml={partHtml}
                                questionType={question.type}
                                correctAnswer={(question as IAnswered).correctAnswer}
                                userAnswer={(question as IAnswered).answer}
                                testType={test?.type}
                              />
                            </div>
                          )}
                          {showAlza && <AlzaBanner id="73217" className="w-full h-24 px-10" />}
                          {showGoogle && (
                            <div className="adsbygoogleparent" style={{position: "relative", width: "100%", maxWidth: "100%", height: "200px"}}>
                              <Adsense
                                client="ca-pub-7116882657059239"
                                slot="4664278820"
                                style={{ display: 'block', textAlign: 'center', position: "absolute", height: "inherit", width: "inherit", maxWidth: "inherit" }}
                                format="fluid"
                                layout="in-feed"
                                responsive="true"
                                {...(window.location.hostname === 'localhost' ? { 'adTest': 'on' } : {})}
                              />
                            </div>
                          )}
                        </Fragment>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {ended ? (
            <div className="w-full flex justify-evenly mobileWidth:flex-col items-center py-4 gap-5">
              <button className="button-primary text-xl text-center w-max px-10 mobileWidth:mb-[86px]" onClick={() => navigate(`/tests/${siteConfig.routePrefix}`)}>
                {siteConfig.labels.test.back}
              </button>
            </div>
          ) : (
            <div className="w-full flex justify-center py-5">
              <button className="button-primary bg-kura text-yellow-300 font-bold text-xl text-center w-max px-10" onClick={onEnd}>
                {siteConfig.labels.test.end}
              </button>
            </div>
          )}
        </div>
      </div>
      {started && isMat && (
        <div className="absolute flex flex-col overflow-hidden h-full max-w-full mobileWidth:w-full mobile:w-full gap-4 z-[1000] right-0 bottom-0 justify-between items-end py-4 px-8">
          <div
            style={{ transform: showFormulas ? 'translate3d(0px,0px,0px)' : 'translate3d(150%,0px,0px)' }}
            className="drop-shadow-xl rounded-xl max-h-[calc(100%-100px)] mobileWidth:w-full mobile:w-full h-max transition-all bg-back rounded-lg relative"
          >
            <div className="w-[600px] mobile:!w-full mobileWidth:!w-full max-w-full overflow-y-auto max-h-full pointer-events-auto">
              <img src={siteConfig.features.formulasImageUrl ?? ''} className="w-full" alt="vzorce" />
            </div>
            <div className="w-full h-full bg-back mix-blend-multiply absolute inset-0" />
          </div>
          <button className="button-primary font-bold text-xl text-center w-max px-10 h-max pointer-events-auto" onClick={() => setShowFormulas((prev) => !prev)}>
            {showFormulas ? siteConfig.labels.test.hideFormulas : siteConfig.labels.test.showFormulas}
          </button>
        </div>
      )}
    </div>
  )
}

export default TestPage
